'use client';

import { useMemo } from 'react';
import { cn, formatTimeAgo } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface RewardTickerItem {
  id: string;
  type: 'reward' | 'sale';
  value: number;
  timestamp: number;
  seatIndex?: number;
  sellerAddress?: string;
  sellerName?: string;
  sellerAvatar?: string;
}

function getRewardTier(value: number) {
  if (value < 1) return { text: 'text-muted-foreground', border: 'border-border/50', dot: 'text-muted-foreground' };
  if (value <= 10) return { text: 'text-win-green', border: 'border-win-green/10', dot: 'text-win-green' };
  if (value <= 50) return { text: 'text-accent-blue-light', border: 'border-accent-blue/15', dot: 'text-accent-blue-light' };
  return { text: 'text-amber-400', border: 'border-amber-500/20', dot: 'text-amber-400' };
}

export function GridRewardsTicker({
  rewardEvents,
  buyoutEvents,
  ethPrice,
}: {
  rewardEvents?: any[];
  buyoutEvents?: any[];
  ethPrice: number;
}) {
  const items = useMemo(() => {
    const all: RewardTickerItem[] = [];

    for (const evt of rewardEvents || []) {
      const ethVal = Number(evt.amount) / 1e18;
      all.push({
        id: evt.id,
        type: 'reward',
        value: ethVal * ethPrice,
        timestamp: evt.timestamp,
      });
    }

    for (const evt of buyoutEvents || []) {
      const usdVal = Number(evt.price) / 1e6;
      all.push({
        id: evt.id,
        type: 'sale',
        value: usdVal,
        timestamp: evt.timestamp,
        seatIndex: evt.seatIndex,
        sellerAddress: evt.oldHolder,
        sellerName: evt.oldHolderIdentity?.displayName,
      });
    }

    return all.sort((a, b) => b.timestamp - a.timestamp);
  }, [rewardEvents, buyoutEvents, ethPrice]);

  if (items.length === 0) return null;

  const pillBase = 'inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] whitespace-nowrap shrink-0';

  return (
    <div className="overflow-x-auto scrollbar-hide -mx-1 px-1">
      <div className="flex items-center gap-1.5 pb-2">
        {items.map((item) => {
          const timeAgo = formatTimeAgo(item.timestamp * 1000);

          if (item.type === 'sale') {
            return (
              <div key={item.id} className={cn(pillBase, 'bg-amber-500/10 border-amber-500/20')}>
                {item.sellerAddress && (
                  <Avatar className="h-3.5 w-3.5">
                    <AvatarImage src={item.sellerAvatar || generateGridAvatarUrl(item.sellerAddress, 16)} />
                    <AvatarFallback className="text-[6px]">{item.sellerName?.[0] || '?'}</AvatarFallback>
                  </Avatar>
                )}
                <span className="text-foreground/80">{item.sellerName || 'Unknown'}</span>
                <span className="text-muted-foreground">sold #{(item.seatIndex ?? 0) + 1}</span>
                <span className="font-bold text-amber-400">+${item.value.toFixed(2)}</span>
                <span className="text-muted-foreground/60">{timeAgo}</span>
              </div>
            );
          }

          const tier = getRewardTier(item.value);
          return (
            <div key={item.id} className={cn(pillBase, tier.border)}>
              <span className={cn('font-semibold', tier.dot)}>●</span>
              <span className="text-foreground/80">Reward</span>
              <span className={cn('font-bold', tier.text)}>+${item.value.toFixed(2)}</span>
              <span className="text-muted-foreground/60">{timeAgo}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
